import { useEffect, useLayoutEffect, useRef } from 'react';
import { fetchQuery, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import type { IndividualStudentReportInitObjectQuery } from '../graphQLOperations/__generated__/IndividualStudentReportInitObjectQuery.graphql';
import { getLearnosityInitObjectIndividualReport } from '../graphQLOperations/IndividualStudentReport';
import { isLearnosityRequest } from '../bffApiCalls/LearnosityFetchError';
import { throwIfAborted } from '../utils/fetchCancellation';
import { LearnosityInitializationPayloadError, LearnosityReportAccessDeniedError } from '../utils/learnosityErrors';
import { captureLearnosityException, classifyLearnosityFailure } from '../utils/learnositySentry';
import type { LearnosityInitializationRefetch } from './useLearnosityItemsInitialization';
import type { LearnosityRequest } from '../models/LearnosityRequest';

function readInitialization(payload: string | null | undefined): LearnosityRequest | null {
  if (payload == null) return null;
  if (payload === '') throw new LearnosityReportAccessDeniedError();
  let value: unknown;
  try {
    value = JSON.parse(payload);
  } catch {
    throw new LearnosityInitializationPayloadError();
  }
  if (!isLearnosityRequest(value)) throw new LearnosityInitializationPayloadError();
  return value;
}

function initialSnapshot(key: string, payload: string | null | undefined) {
  try {
    return { key, initialization: readInitialization(payload) };
  } catch (error) {
    return { key, initialization: null, error };
  }
}

/** Preserve the teacher report's existing Relay authorization and signing source. */
export function useIndividualReportInitialization(
  variables: IndividualStudentReportInitObjectQuery['variables'],
  fetchKey?: string,
) {
  const environment = useRelayEnvironment();
  const data = useLazyLoadQuery<IndividualStudentReportInitObjectQuery>(
    getLearnosityInitObjectIndividualReport,
    variables,
    { fetchPolicy: 'network-only', fetchKey },
  );
  const key = JSON.stringify(variables);
  const source = useRef<ReturnType<typeof initialSnapshot>>();
  const reported = useRef<ReturnType<typeof initialSnapshot>>();
  // fetchQuery publishes renewed signing data to Relay. That store notification
  // must not replace the original request and replenish this player's retry budget.
  const snapshot =
    source.current?.key === key && (source.current.initialization || source.current.error)
      ? source.current
      : initialSnapshot(key, data.getLearnosityInitObject);
  useLayoutEffect(() => {
    // Speculative replacements must not overwrite the running player's signing snapshot.
    source.current = snapshot;
  });
  useEffect(() => {
    if (!snapshot.error || reported.current === snapshot) return;
    reported.current = snapshot;
    // Report committed signing failures without retaining payloads or inventing transport details.
    // The adapter keeps relationship denials ordinary even when they stop startup.
    captureLearnosityException(snapshot.error, {
      api: 'items',
      assessmentType: variables.initModel.renderingType === 'inline' ? 'exit_slip_print' : 'individual_report',
      operation: 'init_object',
      errorCategory: classifyLearnosityFailure(snapshot.error),
      renderPhase: 'before_ready',
      terminalStartup: true,
      sessionId: variables.initModel.sessionId,
      learnerId: variables.initModel.userId,
      activityReference: variables.initModel.activityId,
    });
  }, [snapshot, variables.initModel]);
  const initialization = snapshot.initialization;

  const refetchInitialization: LearnosityInitializationRefetch = async (_initialization, signal) => {
    throwIfAborted(signal);
    return new Promise((resolve, reject) => {
      const subscription = { current: undefined as { unsubscribe: () => void } | undefined };
      let settled = false;
      const finish = (error?: unknown, request?: LearnosityRequest) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener('abort', abort);
        subscription.current?.unsubscribe();
        if (request) resolve(request);
        else reject(error);
      };
      const abort = () => finish(new DOMException('Learnosity signing refresh aborted', 'AbortError'));
      signal.addEventListener('abort', abort, { once: true });
      subscription.current = fetchQuery<IndividualStudentReportInitObjectQuery>(
        environment,
        getLearnosityInitObjectIndividualReport,
        variables,
        {
          fetchPolicy: 'network-only',
          // The recovery hook owns the terminal report, including transport failures.
          networkCacheConfig: { metadata: { learnosityRecovery: true } },
        },
      ).subscribe({
        next: response => {
          try {
            const request = readInitialization(response.getLearnosityInitObject);
            if (!request) throw new LearnosityInitializationPayloadError('missing');
            finish(undefined, request);
          } catch (error) {
            finish(error);
          }
        },
        error: (error: unknown) => finish(error),
        complete: () => finish(new LearnosityInitializationPayloadError('missing')),
      });
      // Relay can deliver synchronously from its store.
      if (settled) subscription.current.unsubscribe();
    });
  };
  return { initialization, refetchInitialization, initializationError: snapshot.error };
}
